/**
 * RegulatoryWarningsStep.js
 *
 * Step 5: Show conflicts with existing regulatory items.
 */

import React from 'react';
import { AlertTriangle, Lock, Check, Info, ArrowRight, Shield } from 'lucide-react';

const RegulatoryWarningsStep = ({ conflicts = [], items = [] }) => {
  const hasConflicts = conflicts.length > 0;
  const lockedConflicts = conflicts.filter(c => c.isLocked);
  const unlockableConflicts = conflicts.filter(c => !c.isLocked);

  if (!hasConflicts) {
    return (
      <div className="p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-slate-800">No Conflicts Detected</h3>
          <p className="text-sm text-slate-500 mt-1">
            The imported items don't conflict with existing regulatory requirements
          </p>
        </div>

        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-start gap-3">
            <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-green-800">Ready to proceed</p>
              <p className="text-sm text-green-700 mt-1">
                {items.length} items ready for import. Continue to request approval.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="text-center mb-6">
        <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <AlertTriangle className="h-8 w-8 text-amber-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">Potential Conflicts Found</h3>
        <p className="text-sm text-slate-500 mt-1">
          {conflicts.length} imported items are similar to existing items
        </p>
      </div>

      {/* Locked regulatory conflicts */}
      {lockedConflicts.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Lock className="h-4 w-4 text-red-500" />
            <h4 className="font-medium text-slate-800">
              Locked Regulatory Items ({lockedConflicts.length})
            </h4>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            These items match locked regulatory requirements and cannot be replaced.
            Your imports will be added as separate items.
          </p>

          <div className="space-y-3">
            {lockedConflicts.map((conflict, index) => (
              <div key={index} className="border border-red-200 rounded-lg overflow-hidden">
                <div className="p-3 bg-red-50 border-b border-red-200">
                  <div className="flex items-center gap-2 text-sm text-red-700">
                    <Lock className="h-4 w-4" />
                    <span className="font-medium">Locked regulatory item</span>
                    <span className="text-xs text-red-500">
                      {Math.round(conflict.similarity * 100)}% similar
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{conflict.existingItem.text}</p>
                </div>
                <div className="p-3 bg-white">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <ArrowRight className="h-4 w-4" />
                    <span>Your imported item</span>
                  </div>
                  <p className="text-sm text-slate-700">{conflict.importedItem.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Non-locked conflicts */}
      {unlockableConflicts.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <Info className="h-4 w-4 text-amber-500" />
            <h4 className="font-medium text-slate-800">
              Similar Items ({unlockableConflicts.length})
            </h4>
          </div>
          <p className="text-sm text-slate-500 mb-3">
            These items are similar to existing custom items. Consider if duplicates are needed.
          </p>

          <div className="space-y-3">
            {unlockableConflicts.map((conflict, index) => (
              <div key={index} className="border border-amber-200 rounded-lg overflow-hidden">
                <div className="p-3 bg-amber-50 border-b border-amber-200">
                  <div className="flex items-center gap-2 text-sm text-amber-700">
                    <span className="font-medium">Existing item</span>
                    <span className="text-xs text-amber-500">
                      {Math.round(conflict.similarity * 100)}% similar
                    </span>
                  </div>
                  <p className="text-sm text-slate-700 mt-2">{conflict.existingItem.text}</p>
                </div>
                <div className="p-3 bg-white">
                  <div className="flex items-center gap-2 text-sm text-slate-500 mb-2">
                    <ArrowRight className="h-4 w-4" />
                    <span>Your imported item</span>
                  </div>
                  <p className="text-sm text-slate-700">{conflict.importedItem.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="p-4 bg-slate-50 rounded-lg">
        <p className="text-sm text-slate-600">
          <strong>What happens next:</strong> Your imported items will be added to the checklist.
          Existing items will remain unchanged. You can manage duplicates later in Settings.
        </p>
      </div>
    </div>
  );
};

export default RegulatoryWarningsStep;
